import { MeshInstance, Entity, GraphNode, Material } from "playcanvas";

/**
 * Allows multiple overrides to be applied to the same target,
 * as long as they are assigned to different override layers.
 * The override with the highest layer number will be rendered.
 * If that override is removed, the override with the next-highest
 * override will be rendered instead, and so on...
 */
export type OverrideLayer = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;

export interface MaterialOverride {
	layer: OverrideLayer;
	material: Material;
	/**
	 * Optional filter for ignoring mesh instances when applying
	 * overrides,  based on their graph nodes.
	 */
	ignoredNodeFilter?: (node: GraphNode) => boolean;
}

class MaterialOverrideManager {
	private _instanceStates = new WeakMap<
		MeshInstance,
		{
			topLayer: OverrideLayer;
			topMaterial: Material;
			originalMaterial: Material;
			layerMaterials: Map<OverrideLayer, Material>;
		}
	>();

	constructor() {
		this.add = this.add.bind(this);
		this.remove = this.remove.bind(this);
		this.toggle = this.toggle.bind(this);
		this._add = this._add.bind(this);
		this._remove = this._remove.bind(this);
	}

	add(override: MaterialOverride, targets: Iterable<Entity | MeshInstance>) {
		this.toggle(true, override, targets);
	}

	remove(override: MaterialOverride, targets: Iterable<Entity | MeshInstance>) {
		this.toggle(false, override, targets);
	}

	toggle(
		toggle: boolean,
		override: MaterialOverride,
		targets: Iterable<Entity | MeshInstance>,
	) {
		const toggleFn = toggle ? this._add : this._remove;

		for (const target of targets) {
			if (target instanceof Entity) {
				const component = target.model ?? target.render;
				if (!component?.meshInstances) {
					continue;
				}
				for (const instance of component.meshInstances) {
					toggleFn(override, instance);
				}
			} else {
				toggleFn(override, target);
			}
		}
	}

	private _add(override: MaterialOverride, instance: MeshInstance) {
		if (override.ignoredNodeFilter?.(instance.node)) {
			return;
		}

		const { layer, material } = override;
		let state = this._instanceStates.get(instance);

		if (!state) {
			state = {
				topLayer: layer,
				topMaterial: material,
				originalMaterial: instance.material,
				layerMaterials: new Map([[layer, material]]),
			};
			this._instanceStates.set(instance, state);
		} else {
			state.layerMaterials.set(layer, material);
			state.topLayer = Math.max(
				...state.layerMaterials.keys(),
			) as OverrideLayer;
			state.topMaterial = state.layerMaterials.get(state.topLayer)!;
		}

		if (instance.material !== state.topMaterial) {
			instance.material = state.topMaterial;
		}
	}

	private _remove(override: MaterialOverride, instance: MeshInstance) {
		if (override.ignoredNodeFilter?.(instance.node)) {
			return;
		}
		const state = this._instanceStates.get(instance);
		if (!state) {
			return;
		}
		const { layer, material } = override;
		if (state.layerMaterials.get(layer) !== material) {
			// Another override on the same layer has replaced the override
			// we're trying to remove, so there's nothing to do.
			return;
		}

		state.layerMaterials.delete(layer);
		const topLayer = Math.max(...state.layerMaterials.keys()) as OverrideLayer;

		if (topLayer >= 0) {
			state.topLayer = topLayer as OverrideLayer;
			state.topMaterial = state.layerMaterials.get(state.topLayer)!;

			if (instance.material !== state.topMaterial) {
				instance.material = state.topMaterial;
			}
		} else {
			this._instanceStates.delete(instance);
			instance.material = state.originalMaterial;
		}
	}
}

// NOTE: This is a singleton since it makes no sense to use multiple
// such managers, they would overwrite each other's overrides
// if applied to the same targets.
export const materialOverrideManager = new MaterialOverrideManager();
